import { ChangeEvent, useState } from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ProductsSequenceProduct } from 'common/contracts';
import { EAssetType, EProductPriority, EProductType } from 'constants/enums';
import { transformQuantityValue } from 'utils/formattingUtil';
import { ProductProps } from 'views/Offers/Offer.types';

import {
  ImageObject,
  Product
} from 'components/ProductsForm/ProductsForm.types';

interface UseProductManagementProps {
  products: ProductProps[];
  setProducts: (products: ProductProps[]) => void;
  setDirtyProducts: (dirty: boolean) => void;
  productsData: { result: Product[] } | undefined;
  formatProductQuantity: UseMutationResult<
    AxiosResponse<string>,
    unknown,
    { quantity: string; productType: EProductType },
    unknown
  >;
}

export const useProductManagement = ({
  products,
  setProducts,
  setDirtyProducts,
  productsData,
  formatProductQuantity
}: UseProductManagementProps) => {
  const [currentProduct, setCurrentProduct] =
    useState<ProductsSequenceProduct | null>(null);

  const handleQuantityChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newQuantity =
      e.target.value.length !== 0
        ? transformQuantityValue(e.target.value)
        : e.target.value;
    setCurrentProduct({
      productId: currentProduct?.productId || '',
      quantity: newQuantity,
      priority: currentProduct?.priority || EProductPriority.SUB
    });
  };

  const handlePriorityChange = (value: string) => {
    setCurrentProduct({
      productId: currentProduct?.productId || '',
      quantity: currentProduct?.quantity || 0,
      priority: value
    });
  };

  const removeProduct = (productId: string) => {
    setDirtyProducts(true);
    const newProducts = products.filter(
      (p: ProductProps) => p.productId !== productId
    );
    setProducts(newProducts);
  };

  const editProduct = (productId: string) => {
    return products.forEach((p: ProductProps) => {
      if (p._id === productId) {
        removeProduct(productId);
        setCurrentProduct({
          product: p.image,
          productId: p._id,
          quantity: p.amount || 0,
          priority: p.priority
        });
      }
    });
  };

  const addProduct = async () => {
    setDirtyProducts(true);
    const foundProduct = productsData!.result.find((pd: Product) => {
      return pd.productId === currentProduct?.productId;
    });
    const formattedProductQuantity = await formatProductQuantity.mutateAsync({
      quantity: currentProduct!.quantity?.toString() || '',
      productType: foundProduct!.type
    });
    setProducts([
      ...products,
      {
        priority: currentProduct?.priority || EProductPriority.SUB,
        _id: currentProduct!.productId!,
        productId: currentProduct!.productId!,
        image: foundProduct!.images[0].url,
        imagePrefix: foundProduct!.images?.find(
          (i: ImageObject) => i.type === EAssetType.PRODUCT_PREFIX
        )?.url || '',
        name: foundProduct!.name,
        amount: currentProduct!.quantity,
        type: foundProduct!.type,
        textFontColorHex: foundProduct!.textFontColorHex || '',
        prefix: foundProduct!.prefix || '',
        suffix: foundProduct!.suffix || '',
        quantityDisplay: formattedProductQuantity.data
      } as ProductProps
    ]);
    setCurrentProduct({
      productId: '',
      quantity: 0,
      priority: EProductPriority.SUB
    });
  };

  return {
    currentProduct,
    setCurrentProduct,
    handleQuantityChange,
    handlePriorityChange,
    removeProduct,
    editProduct,
    addProduct
  };
};
